import { Component, Input, Output, EventEmitter, HostBinding } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Formats action button text to Title / Camel Case:
 * Capitalizes major words (e.g. Add, Invoice, Registration, Order, Customer),
 * while keeping minor articles and prepositions lowercase (e.g. an, a, the, of, for).
 * Examples:
 * - "Add an invoice" -> "Add an Invoice"
 * - "Add a registration" -> "Add a Registration"
 * - "Add A Member" -> "Add a Member"
 */
export function formatActionButtonText(text?: string): string {
  if (!text) return '';
  const str = String(text).trim();
  if (!str) return '';

  const acronyms = new Set([
    'FY', 'ID', 'PAN', 'GST', 'GSTIN', 'URL', 'API', 'OTP', 'PO', 'PR',
    'CVV', 'SSN', 'HRMS', 'TDS', 'TCS', 'CIN', 'LLPIN', 'IEC', 'IFSC',
    'MICR', 'SWIFT', 'NEFT', 'RTGS', 'IMPS', 'UPI', 'SKU', 'HSN', 'SAC',
    'PDF', 'CSV', 'MSME', 'TAN', 'VAT', 'CST', 'SEZ', 'LUT', 'DSO', 'UTR'
  ]);

  let cleaned = str
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ');

  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';

  return words
    .map((word) => {
      const upper = word.toUpperCase();
      if (acronyms.has(upper)) {
        return upper;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

@Component({
  selector: 'app-action-button, app-add-button',
  template: `
    <button
      [type]="type"
      class="app-add-btn btn-icon"
      [ngClass]="[
        'app-add-btn--' + size,
        fullWidth ? 'app-add-btn--full-width' : '',
        disabled ? 'app-add-btn--disabled' : '',
        customClass
      ]"
      [style.width]="width || (fullWidth ? '100%' : null)"
      [style.minWidth]="minWidth || null"
      [style.maxWidth]="maxWidth || null"
      [disabled]="disabled || loading"
      (click)="handleClick($event)"
    >
      <!-- Loading spinner -->
      <span *ngIf="loading" class="app-add-btn__spinner">
        <svg class="app-add-btn__spinner-svg" viewBox="0 0 24 24">
          <circle
            class="app-add-btn__spinner-circle"
            cx="12"
            cy="12"
            r="10"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
          ></circle>
        </svg>
      </span>

      <!-- Plus Icon (Expense exact 20x20 SVG) / Custom Icon -->
      <ng-container *ngIf="!loading && (showIcon && isSvgShow)">
        <img
          *ngIf="icon && (icon.includes('.svg') || icon.includes('/') || icon.includes('http'))"
          [src]="icon"
          class="app-add-btn__icon"
          alt=""
        />
        <i
          *ngIf="icon && !icon.includes('.svg') && !icon.includes('/') && !icon.includes('http')"
          [class]="icon"
          class="app-add-btn__icon"
        ></i>
        <svg
          *ngIf="!icon"
          class="app-add-btn__icon"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 4.16666V15.8333"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M4.16675 10H15.8334"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </ng-container>

      <!-- Label Text / Projected Content -->
      <span class="app-add-btn__label" *ngIf="displayLabel">
        {{ displayLabel }}
      </span>
      <ng-content></ng-content>
    </button>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }
      :host.w-100,
      :host.w-full {
        width: 100%;
        display: block;
      }

      /* Exact Expense AppAddButton & .btn-icon styling */
      .app-add-btn,
      .btn-icon {
        font-family: 'Gilroy', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        height: 36px !important;
        display: inline-flex !important;
        justify-content: center !important;
        align-items: center !important;
        gap: 6px !important;
        border-radius: 6px !important;
        border: 1px solid #0386ff !important;
        background-color: #ffffff !important;
        color: #0386ff !important;
        padding: 0 14px !important;
        font-size: 14px !important;
        font-weight: 600 !important;
        line-height: 1.2 !important;
        cursor: pointer !important;
        box-sizing: border-box !important;
        text-transform: capitalize !important;
        outline: none !important;
        user-select: none !important;
        text-decoration: none !important;
        white-space: nowrap !important;
        box-shadow: none !important;
        transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, color 0.15s ease-in-out !important;

        /* Sizes */
        &--large {
          height: 44px !important;
          padding: 0 18px !important;
          font-size: 15px !important;
        }

        &--medium {
          height: 36px !important;
          padding: 0 14px !important;
          font-size: 14px !important;
        }

        &--small {
          height: 32px !important;
          padding: 0 10px !important;
          font-size: 13px !important;
        }

        &--compact {
          height: 28px !important;
          padding: 0 8px !important;
          font-size: 12px !important;
        }

        &--full-width {
          width: 100% !important;
        }

        /* Hover & Active states */
        &:hover:not(:disabled):not(.app-add-btn--disabled) {
          background-color: #f0f7ff !important;
          border-color: #0386ff !important;
          color: #0386ff !important;
        }

        &:active:not(:disabled):not(.app-add-btn--disabled) {
          background-color: #e0effe !important;
          border-color: #0073e6 !important;
          color: #0073e6 !important;
        }

        /* Disabled state */
        &:disabled,
        &--disabled {
          opacity: 0.5 !important;
          cursor: not-allowed !important;
          pointer-events: none !important;
          box-shadow: none !important;
        }

        &__icon {
          flex-shrink: 0;
          width: 20px;
          height: 20px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #0386ff;
        }

        &__label {
          display: inline-flex;
          align-items: center;
          color: inherit;
          font-size: 14px;
          font-weight: 600;
        }

        &__spinner {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-right: 4px;
        }

        &__spinner-svg {
          width: 16px;
          height: 16px;
          animation: app-add-btn-spin 0.8s linear infinite;
        }

        &__spinner-circle {
          stroke-dasharray: 40;
          stroke-dashoffset: 10;
          stroke-linecap: round;
        }
      }

      @keyframes app-add-btn-spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
})
export class AppActionButtonComponent {
  @Input() label: string = '';
  @Input() labelText: string = '';
  @Input() btnName: string = '';
  @Input() showIcon: boolean = true;
  @Input() isSvgShow: boolean = true;
  @Input() icon: string = '';
  @Input() size: 'large' | 'medium' | 'small' | 'compact' = 'medium';
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() fullWidth: boolean = false;
  @Input() width: string = '';
  @Input() minWidth: string = '';
  @Input() maxWidth: string = '';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() customClass: string = '';
  @Output() btnClick = new EventEmitter<MouseEvent>();

  get displayLabel(): string {
    const raw = this.label || this.btnName || this.labelText;
    return formatActionButtonText(raw);
  }

  @HostBinding('style.width') get hostWidth() {
    if (this.width) return this.width;
    return this.fullWidth ? '100%' : null;
  }
  @HostBinding('style.minWidth') get hostMinWidth() {
    return this.minWidth || null;
  }
  @HostBinding('style.maxWidth') get hostMaxWidth() {
    return this.maxWidth || null;
  }
  @HostBinding('style.display') get hostDisplay() {
    return this.fullWidth ? 'block' : 'inline-block';
  }
  @HostBinding('class.w-100') get hostW100() {
    return this.fullWidth;
  }
  @HostBinding('style.pointerEvents') get hostPointerEvents() {
    return this.disabled ? 'none' : null;
  }

  handleClick(event: MouseEvent): void {
    if (this.disabled || this.loading) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.btnClick.emit(event);
  }
}

export { AppActionButtonComponent as AppAddButtonComponent };
